<template>
  <div class="preview-section">
    <hr>
    <div v-if="selectedElement" class="card">
      <!-- Utilisation du composant PreviewHeader -->
      <cardHeader :numOrder="getQuestionNumber() === 'N/A' ? numOrder : getQuestionNumber()"
        :elementType="selectedElement.el_Type" :mode="'Preview'" :isAgent="isAgent" />

      <div class="card-body">
        <div class="mb-2">
          <h3>{{ selectedElement.el_Text }}</h3>
        </div>
        <div class="quill-editor" ref="quillEditor"></div>

        <div class="AllFeedbacks" v-if="showReport">
          <div v-if="results.score === selectedElement.maxScore && selectedElement.feedback.correct"
            class="PositiveFeebackContainer">
            {{ selectedElement.feedback.correct }}
          </div>
          <div v-else-if="results.score < selectedElement.maxScore && selectedElement.feedback.incorrect"
            class="NegativeFeebackContainer">
            {{ selectedElement.feedback.incorrect }}
          </div>
        </div>

        <div class="mt-4">
          <draggable 
            v-model="shuffledLabels" 
            tag="ol" 
            group="labels" 
            @end="onDragEnd" 
            itemKey="id"
            :disabled="props.readOnly" 
            :class="['sortable-zone', { 'readonly-mode': props.readOnly, flash: flashEffect }]"
            handle=".drag-handle">
            <template #item="{ element, index }">
              <li :key="element.id" class="label-item d-flex justify-content-between align-items-center">
                <span class="drag-handle" title="Move the proposition">☰</span>
                <input disabled type="text" v-model="shuffledLabels[index].text"
                  class="form-control me-2 flex-grow-1" />
                <span class="drag-handle" title="Move the proposition">☰</span>
              </li>
            </template>
          </draggable>
        </div>
        <div class="VorderContainer">
          <button @click="handleValidateClick()">Validate Order</button>
        </div>
        <div class="ReportContainer" v-if="showReport">
          <div class="row">
            <div class="col">
              <h5>Results:</h5>
            </div>
            <div class="col" style="text-align: right;">
              <strong>Date:</strong> {{ currentDate }}
            </div>

            <hr>
            <p hidden><strong>The Right order:</strong>
            <div>{{ selectedElement.labels }}</div>
            </p>
            <p><strong>Your Answers:</strong> {{ results.content }}</p>
            <p><strong>Max-Score:</strong> {{ selectedElement.maxScore }} </p>
            <p><strong>Score:</strong> {{ results.score }}</p>
            <p><strong>Penalties:</strong> {{ results.error }}</p>

          </div>
        </div>
      </div>

      <cardFooterLearningMode v-if="(publicationStore.publicationData?.mode) !== 'exam'" :showReport="showReport"
        :submitAnswers="() => submitAnswers(true)" :closeReport="closeReport" :resetSelections="resetLabels" />
    </div>
  </div>
</template>

<script setup>
import { useResponsesStore } from '../../stores/useResponsesStore.js';
import { usePublicationStore } from '@/stores/usePublicationStore'
import cardHeader from './cardHeader.vue';
import { ref, computed, onMounted } from 'vue';
import draggable from 'vuedraggable';
import Quill from 'quill';
import cardFooterLearningMode from './cardFooterLearningMode.vue';

const publicationStore = usePublicationStore()
defineEmits(['interaction-started'])
const props = defineProps({
  question: {
    type: Object,
    required: true,
  },
  testData: {
    type: Object,
    required: true,
  },
  numOrder: [Number, null],

  questionIndexMap: {
    type: Object,
    required: false
  },
  readOnly: { type: Boolean, default: false },
});

const selectedElement = computed(() => props.question);
const responsesStore = useResponsesStore();
const shuffledLabels = ref([]);
const quillEditor = ref(null);
let quillInstance = null;
const showReport = ref(false);
const token = localStorage.getItem('token'); // Récupérer le token depuis le stockage local
const decodedToken = decodeToken(token);
const userId = decodedToken?._id || decodedToken?.sub;
const isAgent = ref(decodedToken?.agent === true);
const agentLevel = ref(decodedToken?.agentLevel || null);

const orderedLabels = ref([]);

const flashEffect = ref(false);

// Fonction pour auto-réponse basée sur l'agentLevel pour l'interaction Order
const autoAnswerForAgent = () => {
  if (!isAgent.value || !agentLevel.value || !selectedElement.value?.labels) return;
  
  const correctOrder = selectedElement.value.labels; // L'ordre correct
  
  if (agentLevel.value === 'A') {
    // Niveau A: mettre dans le bon ordre (85-100% succès)
    shuffledLabels.value = correctOrder.map((text, index) => ({
      id: Math.random().toString(36).substr(2, 9),
      text: text
    }));
  } else if (agentLevel.value === 'B') {
    // Niveau B: taux de succès ciblé 40-84% (moyenne ~62%)
    // 38% de chances de donner un mauvais ordre
    const shouldGiveWrongOrder = Math.random() < 0.38;
    
    if (shouldGiveWrongOrder) {
      // Créer un ordre partiellement incorrect
      const partiallyIncorrectOrder = [...correctOrder];
      // Échanger quelques éléments pour créer des erreurs
      const numSwaps = Math.floor(correctOrder.length / 2); // Échanger environ la moitié
      for (let i = 0; i < numSwaps; i++) {
        const idx1 = Math.floor(Math.random() * correctOrder.length);
        const idx2 = Math.floor(Math.random() * correctOrder.length);
        if (idx1 !== idx2) {
          [partiallyIncorrectOrder[idx1], partiallyIncorrectOrder[idx2]] = 
          [partiallyIncorrectOrder[idx2], partiallyIncorrectOrder[idx1]];
        }
      }
      shuffledLabels.value = partiallyIncorrectOrder.map((text) => ({
        id: Math.random().toString(36).substr(2, 9),
        text: text
      }));
    } else {
      // Donner le bon ordre
      shuffledLabels.value = correctOrder.map((text) => ({
        id: Math.random().toString(36).substr(2, 9),
        text: text
      }));
    }
  } else if (agentLevel.value === 'C') {
    // Niveau C: taux de succès ciblé <40% (moyenne ~20%)  
    // 80% de chances de donner un mauvais ordre
    const shouldGiveWrongOrder = Math.random() < 0.8;
    
    if (shouldGiveWrongOrder) {
      // Mélanger complètement pour avoir un ordre très incorrect
      const incorrectOrder = [...correctOrder].sort(() => Math.random() - 0.5);
      
      // S'assurer que l'ordre est différent du correct (au moins une position différente)
      let attempts = 0;
      while (incorrectOrder.every((label, index) => label === correctOrder[index]) && attempts < 10) {
        incorrectOrder.sort(() => Math.random() - 0.5);
        attempts++;
      }
      
      shuffledLabels.value = incorrectOrder.map((text) => ({
        id: Math.random().toString(36).substr(2, 9),
        text: text
      }));
    } else {
      // Donner le bon ordre
      shuffledLabels.value = correctOrder.map((text) => ({
        id: Math.random().toString(36).substr(2, 9),
        text: text
      }));
    }
  }
  
  // Déclencher automatiquement la validation après un court délai
  setTimeout(() => {
    handleValidateClick();
  }, 1000);
};

const handleValidateClick = () => {
  flashEffect.value = true;
  submitAnswers(false);

  setTimeout(() => {
    flashEffect.value = false;
  }, 500); // durée du flash
};

// Récupère le numéro de la question depuis questionIndexMap
const getQuestionNumber = () => {
  if (!props.questionIndexMap || typeof props.questionIndexMap !== 'object') {
    console.error("❌ questionIndexMap est indéfini ou n'est pas un objet.");
    return "N/A";
  }

  return props.questionIndexMap[props.question.el_ID] ?? "N/A";
};


const results = ref({
  score: 0,
  error: 0,
  content: '',
});

const currentDate = computed(() => {
  const now = new Date();
  return now.toLocaleString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
});

// Mélanger les étiquettes
const shuffleArray = (array) => {
  return array
    .map((item) => ({ id: Math.random().toString(36).substr(2, 9), text: item }))
    .sort(() => Math.random() - 0.5);
};

const initializeLabels = () => {
  const savedResponse = responsesStore.getResponseForQuestion(userId, selectedElement.value.el_ID);

  if (savedResponse && savedResponse.labelsOrder && savedResponse.labelsOrder.length) {
    console.log('✅ Restaurer ordre depuis Pinia:', savedResponse.labelsOrder);
    shuffledLabels.value = savedResponse.labelsOrder.map((labelText) => ({
      id: Math.random().toString(36).substr(2, 9),
      text: labelText,
    }));
  } else {
    console.log('🔀 Aucun ordre enregistré, on mélange');
    if (selectedElement.value?.labels) {
      const initialOrder = selectedElement.value.labels.map(text => ({
        id: Math.random().toString(36).substr(2, 9),
        text
      })).sort(() => Math.random() - 0.5);

      shuffledLabels.value = initialOrder;

      // Sauvegarde immédiate comme réponse par défaut
      const response = {
        userId: userId,
        questionId: selectedElement.value.el_ID,
        questionLabel: selectedElement.value.el_Text,
        type: selectedElement.value.el_Type,
        labelsOrder: initialOrder.map(label => label.text),
        timestamp: new Date().toISOString(),
        answered: false,
        score: 0, // on laisse à 0 par défaut ici
        maxScore: selectedElement.value.maxScore,
        penalties: 0,
      };

      //responsesStore.saveResponse(response);
      console.log('💾 Ordre initial enregistré comme réponse par défaut');
    }
  }
};


onMounted(() => {

   if (selectedElement.value && selectedElement.value.penalties === undefined) {
    selectedElement.value.penalties = 0; // Valeur par défaut
  }


  initializeLabels();

  if (quillEditor.value) {
    quillInstance = new Quill(quillEditor.value, {
      theme: 'snow',
      readOnly: true,
      modules: { toolbar: false },
    });

    if (selectedElement.value?.el_RichText) {
      quillInstance.root.innerHTML = selectedElement.value.el_RichText;
    }
  }
  
  // Auto-réponse pour les agents après un délai pour laisser l'interface se charger
  if (isAgent.value && agentLevel.value) {
    setTimeout(() => {
      autoAnswerForAgent();
    }, 1500); // Délai plus long pour l'ordre car plus complexe à visualiser
  }
});


// Calculer le score et sauvegarder
const submitAnswers = (showR = false) => {
  const correctOrder = selectedElement.value.labels;
  const studentOrder = shuffledLabels.value.map(label => label.text); // Liste complète

  results.value.content = [...studentOrder]; // Sauvegarde complète de l'ordre actuel

  const isCorrect =
    studentOrder.length === correctOrder.length &&
    studentOrder.every((label, index) => label === correctOrder[index]);

  // Utilisation du système de score et pénalités dynamiques
  const maxScore = selectedElement.value.maxScore || 1;
  const penalties = selectedElement.value.penalties || 0;

  results.value.score = isCorrect ? maxScore : 0;
  results.value.error = isCorrect ? 0 : penalties; // Utilise les pénalités définies

  const response = {
    userId: userId,
    questionId: selectedElement.value.el_ID,
    questionLabel: selectedElement.value.el_Text,
    type: selectedElement.value.el_Type,
    labelsOrder: [...studentOrder], // Liste complète garantie
    timestamp: new Date().toISOString(),
    answered: true,
    score: results.value.score || 0,
    maxScore: maxScore,
    penalties: results.value.error || 0 // Ajouter les pénalités dans la réponse
  };

  responsesStore.saveResponse(response);
  showReport.value = showR;
};


const resetLabels = () => {
  initializeLabels(); // Réinitialiser à un ordre aléatoire
  showReport.value = false;

  const response = {
    userId: userId,
    questionId: selectedElement.value.el_ID,
    labelsOrder: [],
    timestamp: new Date().toISOString(),
    answered: false,
  };

  responsesStore.saveResponse(response);
};

const closeReport = () => {
  showReport.value = false;
};

const onDragEnd = () => {
  const studentOrder = shuffledLabels.value.map(label => label.text);

 const response = {
    userId: userId,
    questionLabel: selectedElement.value.el_Text,
    questionId: selectedElement.value.el_ID,
    type: selectedElement.value.el_Type,
    labelsOrder: [...studentOrder], // Toujours inclure l'ensemble des positions
    score: results.value.score || 0,
    maxScore: selectedElement.value.maxScore || 1,
    penalties: results.value.error || 0, // Ajouter les pénalités
    timestamp: new Date().toISOString(),
    answered: false, // ✅ Pas encore validé, juste réorganisé
  };

  responsesStore.saveResponse(response);
  console.log('Updated labels after drag-and-drop:', studentOrder);
};



function decodeToken(token) {
  if (!token) {
    return null;
  }
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
}
</script>

<style scoped>
.label-item {
  padding: 5px;
  border: 1px solid #ccc;
  margin-bottom: 5px;
  background-color: #f9f9f9;
  cursor: move;
}


  /* Cacher l'élément clipboard de Quill */
  .quill-editor :deep(.ql-clipboard) {
  display: none !important;
}

.PositiveFeebackContainer {
  background-color: rgb(222, 246, 224);
  margin: 10px;
  padding: 10px;
  border: 1px green dotted;
  border-radius: 5px;
}

.NegativeFeebackContainer {
  background-color: rgb(246, 234, 235);
  margin: 10px;
  padding: 10px;
  border: 1px rgb(253, 97, 206) dotted;
  border-radius: 5px;
}

.ReportContainer {
  border: grey 1px solid;
  border-radius: 3px;
  padding: 20px;
  background-color: rgb(54, 63, 67);
  color: greenyellow;
}

.readonly-mode {
  opacity: 0.6;
  pointer-events: none;
}

.readonly-mode .label-item {
  cursor: not-allowed;
}

.VorderContainer {
  text-align: center;
  margin-top: 10px;
  margin-bottom: 10px;
}

.VorderContainer button {
  border: 1px grey solid;
  border-radius: 5px;

}

.VorderContainer button:hover {
  background-color: grey;
  color: white;
}

.drag-handle {
  cursor: grab;
  font-size: 1.2rem;
  color: #888;
  padding: 0 8px;
  user-select: none;
}
.drag-handle:hover {
  color: #000;
}

.sortable-zone {
  transition: background-color 0.3s ease;
}

.flash {
  background-color: rgba(173, 216, 230, 0.4); /* bleu clair temporaire */
}

.flash input {
  font-weight: bold;
}

</style>